(function () {
  'use strict';

  /*
   * Prevent duplicate custom-element registration.
   */
  if (customElements.get('magio-quick-view-modal')) return;


  customElements.define(
    'magio-quick-view-modal',

    class MagioQuickViewModal extends HTMLElement {

      constructor() {
        super();

        /*
         * Modal panel where Shopify product HTML is injected.
         */
        this.panel = this.querySelector(
          '[data-magio-quick-view-panel]'
        );

        /*
         * Dialog element.
         */
        this.dialog = this.querySelector(
          '.magio-quick-view-modal__dialog'
        );

        /*
         * Element that opened the modal.
         */
        this.openedBy = null;

        /*
         * Store page scroll position so that we can restore
         * the exact position after closing.
         */
        this.savedScrollY = 0;

        /*
         * Prevent duplicate body-lock operations.
         */
        this.isScrollLocked = false;


        /*
         * =====================================================
         * CLOSE BUTTONS
         * =====================================================
         */

        this.querySelectorAll(
          '[data-magio-quick-view-close]'
        ).forEach((button) => {

          button.addEventListener('click', (event) => {

            event.preventDefault();

            this.hide();

          });

        });


        /*
         * =====================================================
         * ESCAPE KEY
         * =====================================================
         */

        this._handleKeyup = (event) => {

          if (
            event.code === 'Escape' &&
            this.hasAttribute('open')
          ) {

            event.preventDefault();

            this.hide();

          }

        };


        document.addEventListener(
          'keyup',
          this._handleKeyup
        );


        /*
         * =====================================================
         * CLICK OUTSIDE MODAL
         * =====================================================
         *
         * Anything clicked outside the dialog closes the modal.
         *
         * This covers:
         * - backdrop
         * - empty overlay space
         * - anything else outside the white modal
         */

        this.addEventListener('click', (event) => {

          if (!this.hasAttribute('open')) {
            return;
          }

          /*
           * If there is a dialog and the clicked element is
           * inside it, do nothing.
           */
          if (
            this.dialog &&
            this.dialog.contains(event.target)
          ) {
            return;
          }

          /*
           * Otherwise the click happened outside.
           */
          this.hide();

        });


        /*
         * =====================================================
         * BACKDROP CLICK
         * =====================================================
         *
         * Explicitly support the backdrop as well.
         */

        const backdrop = this.querySelector(
          '.magio-quick-view-modal__backdrop'
        );

        if (backdrop) {

          backdrop.addEventListener(
            'click',
            (event) => {

              event.preventDefault();
              event.stopPropagation();

              this.hide();

            }
          );

        }

      }


      /*
       * =====================================================
       * MOVE MODAL TO BODY
       * =====================================================
       *
       * This prevents collection/card containers,
       * transforms and overflow rules from interfering
       * with position: fixed.
       */

      connectedCallback() {

        if (this.moved) return;

        this.moved = true;

        document.body.appendChild(this);

      }


      /*
       * =====================================================
       * BODY SCROLL LOCK
       * =====================================================
       */

      lockPageScroll() {

        if (this.isScrollLocked) {
          return;
        }

        this.savedScrollY =
          window.scrollY ||
          window.pageYOffset ||
          0;

        const body = document.body;

        /*
         * Freeze body exactly where it currently is.
         *
         * Using position: fixed rather than only
         * overflow:hidden prevents the page from jumping.
         */

        body.style.position = 'fixed';

        body.style.top =
          `-${this.savedScrollY}px`;

        body.style.left = '0';

        body.style.right = '0';

        body.style.width = '100%';

        body.style.overflow = 'hidden';

        /*
         * Also lock the document itself.
         */
        document.documentElement.classList.add(
          'magio-quick-view-open'
        );

        body.classList.add(
          'magio-quick-view-open'
        );

        this.isScrollLocked = true;

      }


      /*
       * =====================================================
       * RESTORE PAGE SCROLL
       * =====================================================
       */

      unlockPageScroll() {

        if (!this.isScrollLocked) {
          return;
        }

        const body = document.body;

        /*
         * Remove temporary body styles.
         */

        body.style.position = '';

        body.style.top = '';

        body.style.left = '';

        body.style.right = '';

        body.style.width = '';

        body.style.overflow = '';

        document.documentElement.classList.remove(
          'magio-quick-view-open'
        );

        body.classList.remove(
          'magio-quick-view-open'
        );

        /*
         * Restore the exact position where the user was.
         */

        const scrollY = this.savedScrollY;

        window.scrollTo(
          0,
          scrollY
        );

        this.isScrollLocked = false;

      }


      /*
       * =====================================================
       * PRODUCT IMAGE GALLERY
       * =====================================================
       */

      initGallery() {

        if (!this.panel) return;


        const slides =
          this.panel.querySelectorAll(
            '[data-qv-slide]'
          );


        const dots =
          this.panel.querySelectorAll(
            '[data-qv-dot]'
          );


        const prev =
          this.panel.querySelector(
            '[data-qv-prev]'
          );


        const next =
          this.panel.querySelector(
            '[data-qv-next]'
          );


        /*
         * Nothing to initialize if there are no images.
         */

        if (!slides.length) return;


        let current = 0;


        /*
         * =====================================================
         * UPDATE GALLERY
         * =====================================================
         */

        const update = (index) => {

          /*
           * IMPORTANT:
           *
           * Do NOT use modulo here.
           *
           * Previously:
           *
           * (index + slides.length) % slides.length
           *
           * caused:
           *
           * last -> first
           * first -> last
           *
           * when arrows were clicked.
           *
           * Now the gallery stops at the boundaries.
           */

          current = Math.max(
            0,
            Math.min(
              index,
              slides.length - 1
            )
          );


          /*
           * ===================================================
           * SLIDES
           * ===================================================
           */

          slides.forEach(
            (slide, index) => {

              const isActive =
                index === current;

              slide.classList.toggle(
                'is-active',
                isActive
              );

            }
          );


          /*
           * ===================================================
           * DOTS
           * ===================================================
           */

          dots.forEach(
            (dot, index) => {

              const isActive =
                index === current;

              dot.classList.toggle(
                'is-active',
                isActive
              );

              dot.setAttribute(
                'aria-selected',
                isActive
                  ? 'true'
                  : 'false'
              );

            }
          );


          /*
           * ===================================================
           * ARROW STATES
           * ===================================================
           */

          const isFirst =
            current === 0;

          const isLast =
            current === slides.length - 1;


          /*
           * PREVIOUS
           */

          if (prev) {

            prev.classList.toggle(
              'is-disabled',
              isFirst
            );

            prev.setAttribute(
              'aria-disabled',
              isFirst
                ? 'true'
                : 'false'
            );

            /*
             * Helpful for accessibility.
             */
            prev.title =
              isFirst
                ? 'Already at first image'
                : 'Previous image';

          }


          /*
           * NEXT
           */

          if (next) {

            next.classList.toggle(
              'is-disabled',
              isLast
            );

            next.setAttribute(
              'aria-disabled',
              isLast
                ? 'true'
                : 'false'
            );

            next.title =
              isLast
                ? 'Already at last image'
                : 'Next image';

          }

        };


        /*
         * =====================================================
         * PREVIOUS IMAGE
         * =====================================================
         */

        if (prev) {

          prev.addEventListener(
            'click',
            (event) => {

              event.preventDefault();

              /*
               * Stop at first image.
               */

              if (current === 0) {
                return;
              }

              update(
                current - 1
              );

            }
          );

        }


        /*
         * =====================================================
         * NEXT IMAGE
         * =====================================================
         */

        if (next) {

          next.addEventListener(
            'click',
            (event) => {

              event.preventDefault();

              /*
               * Stop at last image.
               */

              if (
                current ===
                slides.length - 1
              ) {
                return;
              }

              update(
                current + 1
              );

            }
          );

        }


        /*
         * =====================================================
         * PAGINATION DOTS
         * =====================================================
         */

        dots.forEach(
          (dot) => {

            dot.addEventListener(
              'click',
              (event) => {

                event.preventDefault();

                const index =
                  Number(
                    dot.dataset.qvDot
                  );

                if (
                  Number.isNaN(index)
                ) {
                  return;
                }

                update(index);

              }
            );

          }
        );


        /*
         * Start from first image.
         */

        update(0);

      }


      /*
       * =====================================================
       * OPEN QUICK VIEW
       * =====================================================
       */

      show(opener) {

        /*
         * ===================================================
         * MOBILE BLOCK
         * ===================================================
         *
         * Quick View must not exist on mobile.
         */

        if (
          window.matchMedia(
            '(max-width: 749px)'
          ).matches
        ) {

          return;

        }


        if (
          !opener ||
          !this.panel
        ) {

          return;

        }


        /*
         * Store opener.
         */

        this.openedBy = opener;


        /*
         * Disable opener while loading.
         */

        opener.setAttribute(
          'aria-disabled',
          'true'
        );

        opener.setAttribute(
          'aria-expanded',
          'true'
        );


        opener.classList.add(
          'loading'
        );


        opener
          .querySelector(
            '.loading__spinner'
          )
          ?.classList.remove(
            'hidden'
          );


        /*
         * Get product URL.
         */

        const rawProductUrl =
          opener.getAttribute(
            'data-product-url'
          );


        if (!rawProductUrl) {

          this.resetOpener();

          return;

        }


        const productUrl =
          rawProductUrl
            .split('?')[0];


        /*
         * Clear old product before loading.
         *
         * This guarantees that the previous product
         * cannot remain visible while another product
         * is being requested.
         */

        this.panel.innerHTML = '';


        /*
         * Shopify Section Rendering API.
         */

        fetch(
          `${productUrl}?section_id=magio-quick-view`
        )

          .then(
            (response) => {

              if (!response.ok) {

                throw new Error(
                  `Quick View request failed: ${response.status}`
                );

              }

              return response.text();

            }
          )


          .then(
            (responseText) => {

              /*
               * Parse Shopify response.
               */

              const html =
                new DOMParser()
                  .parseFromString(
                    responseText,
                    'text/html'
                  );


              /*
               * Find product-info.
               */

              const productInfo =
                html.querySelector(
                  'product-info'
                );


              /*
               * Insert product content.
               */

              if (productInfo) {

                HTMLUpdateUtility.setInnerHTML(
                  this.panel,
                  productInfo.outerHTML
                );

              } else {

                /*
                 * Fallback if product-info
                 * is not returned.
                 */

                const section =
                  html.querySelector(
                    '.shopify-section'
                  );


                HTMLUpdateUtility.setInnerHTML(
                  this.panel,
                  section
                    ? section.innerHTML
                    : responseText
                );

              }


              /*
               * Initialize gallery after
               * product HTML has been inserted.
               */

              this.initGallery();


              /*
               * Open modal.
               */

              this.setAttribute(
                'open',
                ''
              );


              /*
               * Prevent background page scrolling.
               */

              this.lockPageScroll();


              /*
               * Accessibility.
               */

              this.setAttribute(
                'aria-hidden',
                'false'
              );


              /*
               * Focus trap if Shopify theme
               * provides it.
               */

              if (
                typeof trapFocus ===
                  'function' &&
                this.dialog
              ) {

                trapFocus(
                  this,
                  this.dialog
                );

              }

            }
          )


          .catch(
            (error) => {

              console.error(
                'Magio quick view failed to load',
                error
              );

              /*
               * Make sure a failed request does not
               * leave the page locked.
               */

              this.hide();

            }
          )


          .finally(
            () => {

              this.resetOpener();

            }
          );

      }


      /*
       * =====================================================
       * RESET OPENER
       * =====================================================
       *
       * This fixes the issue where the product card/button
       * can visually retain its Quick View state after closing.
       */

      resetOpener() {

        const opener =
          this.openedBy;


        if (!opener) {
          return;
        }


        opener.removeAttribute(
          'aria-disabled'
        );


        opener.setAttribute(
          'aria-expanded',
          'false'
        );


        opener.classList.remove(
          'loading'
        );


        /*
         * Remove possible active states used by
         * collection/card implementations.
         */

        opener.classList.remove(
          'active',
          'is-active',
          'quick-view-active',
          'quick-view-open'
        );


        opener
          .querySelector(
            '.loading__spinner'
          )
          ?.classList.add(
            'hidden'
          );

      }


      /*
       * =====================================================
       * CLOSE QUICK VIEW
       * =====================================================
       */

      hide() {
        if (!this.hasAttribute('open')) {
          return;
        }

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');

        const opener = this.openedBy;

        // Release the focus trap WITHOUT returning focus to the opener.
        // (Passing the opener here is what kept the card in its
        // focused/"quick view" state.)
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }

        if (opener) {
          const card = opener.closest(
            '.card-wrapper, .card, .product-card, [data-product-card]'
          );

          // Drop focus from the opener and anything focused in the card.
          opener.blur();

          const active = document.activeElement;

          if (
            active &&
            active !== document.body &&
            (opener.contains(active) || (card && card.contains(active)))
          ) {
            active.blur();
          }

          // Reset the opener state.
          opener.removeAttribute('aria-disabled');
          opener.setAttribute('aria-expanded', 'false');

          opener.classList.remove(
            'loading',
            'active',
            'is-active',
            'quick-view-active',
            'quick-view-open'
          );

          opener
            .querySelector('.loading__spinner')
            ?.classList.add('hidden');

          // Reset any active state applied to the product card.
          if (card) {
            card.classList.remove(
              'active',
              'is-active',
              'quick-view-active',
              'quick-view-open'
            );
          }
        }

        // Remove loaded product HTML.
        if (this.panel) {
          this.panel.innerHTML = '';
        }

        // Restore page scrolling.
        this.unlockPageScroll();

        this.openedBy = null;
      }

    }

  );


  /*
   * =========================================================
   * QUICK VIEW CLICK HANDLER
   * =========================================================
   */

  document.addEventListener(
    'click',
    (event) => {

      /*
       * Find Quick View button.
       */

      const opener =
        event.target.closest(
          '[data-magio-quick-view]'
        );


      if (!opener) {
        return;
      }


      /*
       * NEVER open Quick View on mobile.
       */

      if (
        window.matchMedia(
          '(max-width: 749px)'
        ).matches
      ) {

        event.preventDefault();

        return;

      }


      /*
       * Prevent product-card navigation.
       */

      event.preventDefault();

      event.stopPropagation();


      /*
       * Get modal selector.
       */

      const modalSelector =
        opener.getAttribute(
          'data-modal'
        );


      if (!modalSelector) {
        return;
      }


      /*
       * Find modal.
       */

      const modal =
        document.querySelector(
          modalSelector
        );


      /*
       * Open it.
       */

      if (
        modal &&
        typeof modal.show ===
          'function'
      ) {

        modal.show(
          opener
        );

      }

    }
  );

})();